import React from 'react';
import {
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  Fade,
  Box,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface TooltipTriggerModalProps {
  tooltipText: string;
  modalTitle: string;
  children: React.ReactNode;
  placement?:
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left'
    | 'left-end'
    | 'left-start'
    | 'right'
    | 'right-end'
    | 'right-start'
    | 'top'
    | 'top-end'
    | 'top-start';
}

export default function TooltipTriggerModal({
  tooltipText,
  modalTitle,
  children,
  placement = 'right',
}: TooltipTriggerModalProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = () => setOpen(true);
  const handleClose = (_?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setOpen(false);
      return;
    }
    setOpen(false);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <>
      <Tooltip title={tooltipText} placement={placement} disableFocusListener disableTouchListener>
        <IconButton aria-label={modalTitle} size="small" onClick={handleOpen} onKeyDown={onKeyDown}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              {modalTitle}
            </Typography>
            <IconButton aria-label="Close" onClick={() => setOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>{children}</DialogContent>
      </Dialog>
    </>
  );
}
